import React, { useState, useCallback, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContextAWS';
import { usePlayerProfile } from '../../contexts/PlayerProfileContext';
import { getRosteredPlayers } from '../../utils/rosterManager';
import playerService from '../../services/playerService';
import { mapStatsFromAPI } from '../../services/fantasyDataService';
import { USE_ROSTER_FIXTURES } from '../../services/rosterFixtures';
import { getSampleFreeAgents } from '../../services/playersFixtures';
import { buildFallbackMlbGames, getPlayerGameStatus } from '../../services/mlbGameStatus';
import { useTestDataBanner } from '../../contexts/TestDataBannerContext';
import PlayerSilhouette from '../../components/PlayerSilhouette';
import { PlusIcon } from '../../components/icons/StreamlineFlexSolid';

const PAGE_SIZE = 50;

// ---- Deterministic mock stat (stable across renders so sort works) ----
function hashStr(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function seededRand(seed) {
  const x = Math.sin(seed + 1) * 10000;
  return x - Math.floor(x);
}
function mockStat(playerId, key) {
  return seededRand(hashStr(String(playerId) + key));
}

// ---- Prospect rank helpers ----
function getProspectRank(player, ...aliases) {
  if (!player.prospectRankings) return null;
  const keys = Object.keys(player.prospectRankings);
  for (const alias of aliases) {
    const lo = alias.toLowerCase();
    const k = keys.find(k => { const kl = k.toLowerCase(); return kl === lo || kl.includes(lo) || lo.includes(kl); });
    if (k != null) return player.prospectRankings[k];
  }
  return null;
}
function getAvgRank(player) {
  if (!player.prospectSources?.length) return null;
  return Math.round(player.prospectSources.reduce((s, x) => s + x.rank, 0) / player.prospectSources.length);
}

function formatDateHeader(date) {
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
}

// Player rating scale (Baseball Savant style): a diverging blue→white→red scale
// with saturated extremes. Ratings run ~40..80: low = deep blue, ~average =
// near-white, high = deep red. Text is white or near-black by luminance so the
// deeper backgrounds stay legible.
function ratingHeatColor(rating) {
  const t = Math.max(0, Math.min(1, (rating - 40) / 40)); // 0 low .. 1 high
  const lerp = (a, b, u) => Math.round(a + (b - a) * u);
  let R, G, B;
  if (t < 0.5) { const u = t / 0.5; R = lerp(40, 247, u); G = lerp(94, 247, u); B = lerp(200, 249, u); }
  else { const u = (t - 0.5) / 0.5; R = lerp(247, 205, u); G = lerp(247, 42, u); B = lerp(249, 42, u); }
  const lum = 0.299 * R + 0.587 * G + 0.114 * B;
  return { bg: `rgb(${R}, ${G}, ${B})`, fg: lum > 150 ? '#17150f' : '#ffffff' };
}

// Fixed column widths (px) for the leading columns.
const COL_W = { player: 200, game: 120, rating: 68, owned: 68 };

// ---- Sort indicator ----
function SortTh({ label, col, sortConfig, onSort, align = 'right', minWidth, width }) {
  const active = sortConfig.col === col;
  return (
    <th
      onClick={() => onSort(col)}
      style={{
        textAlign: align,
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        letterSpacing: '-0.022em',
        minWidth,
        width,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
        {label}
        <span style={{
          fontSize: '9px',
          lineHeight: 1,
          color: active ? 'var(--primary)' : 'var(--muted)',
          opacity: active ? 1 : 0.45,
          fontWeight: '700',
        }}>
          {active ? (sortConfig.dir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </span>
    </th>
  );
}

// Rank columns: lower number = better, so first click → asc (rank 1 at top)
const RANK_COLS = new Set(['avgrank', 'mlb', 'ba', 'bp']);

function LeaguePlayers() {
  const { league } = useOutletContext();
  const { user } = useAuth();
  const { openPlayerProfile } = usePlayerProfile();
  const navigate = useNavigate();
  // Season-phase test-data mode + today's games (same shared source as the roster
  // and live-matchup pages, so a player's game state is consistent everywhere).
  const { mode: phase } = useTestDataBanner();
  const mlbGames = React.useMemo(() => buildFallbackMlbGames(phase), [phase]);

  const [pendingAdd, setPendingAdd] = useState(null); // player awaiting add confirmation
  const [players, setPlayers] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [pageTokens, setPageTokens] = useState([null]);
  const [playerStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [viewMode, setViewMode] = useState('stats');
  const [statPeriod, setStatPeriod] = useState('season');
  const [researchSubTab, setResearchSubTab] = useState('trends');
  const [scheduleSubTab, setScheduleSubTab] = useState('week');
  const [sortConfig, setSortConfig] = useState({ col: null, dir: 'desc' });

  const [filters, setFilters] = useState({
    position: '',
    team: '',
    search: '',
    playerType: 'all',
    playerFilter: 'available',
  });

  const leagueId = league?.id || league?.leagueId;
  const rosteredPlayerIds = React.useMemo(() => getRosteredPlayers(leagueId), [leagueId]);

  const today = new Date();
  const scheduleDays = Array.from({ length: scheduleSubTab === 'full' ? 21 : 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return d;
  });

  // Reset sort when switching tabs (columns change)
  useEffect(() => { setSortConfig({ col: null, dir: 'desc' }); }, [viewMode, researchSubTab]);

  const handleSort = (col) => {
    setSortConfig(prev => {
      if (prev.col === col) return { col, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
      return { col, dir: RANK_COLS.has(col) ? 'asc' : 'desc' };
    });
  };

  // ---- Get sort key value for a player ----
  const getSortValue = (player, col) => {
    switch (col) {
      // Stats
      case 'avg':  return player.stats?.avg > 0 ? player.stats.avg : null;
      case 'hr':   return player.stats?.hr ?? null;
      case 'rbi':  return player.stats?.rbi ?? null;
      case 'r':    return player.stats?.r ?? null;
      case 'sb':   return player.stats?.sb ?? null;
      case 'era':  return player.stats?.era > 0 ? player.stats.era : null;
      case 'whip': return player.stats?.whip > 0 ? player.stats.whip : null;
      case 'k':    return player.stats?.k ?? null;
      case 'w':    return player.stats?.w ?? null;
      case 'sv':   return player.stats?.sv ?? null;
      // Trends (hitters) — deterministic mock, scaled integers
      case 'obp':  return Math.floor(mockStat(player.id, 'obp') * 200 + 300);
      case 'slg':  return Math.floor(mockStat(player.id, 'slg') * 200 + 400);
      case 'ops':  return Math.floor(mockStat(player.id, 'ops') * 300 + 700);
      case 'woba': return Math.floor(mockStat(player.id, 'woba') * 100 + 300);
      case 'wrc':  return Math.floor(mockStat(player.id, 'wrc') * 50 + 80);
      // Trends (pitchers)
      case 'fip':  return Math.floor(mockStat(player.id, 'fip') * 200 + 300);
      case 'xfip': return Math.floor(mockStat(player.id, 'xfip') * 200 + 300);
      case 'k9':   return Math.floor(mockStat(player.id, 'k9') * 400 + 800);
      case 'bb9':  return Math.floor(mockStat(player.id, 'bb9') * 200 + 200);
      case 'hr9':  return Math.floor(mockStat(player.id, 'hr9') * 50 + 80);
      // Ratings
      case 'contact': return Math.floor(mockStat(player.id, 'contact') * 20 + 50);
      case 'power':   return Math.floor(mockStat(player.id, 'power') * 20 + 50);
      case 'speed':   return Math.floor(mockStat(player.id, 'speed') * 20 + 50);
      case 'arm':     return Math.floor(mockStat(player.id, 'arm') * 20 + 50);
      case 'field':   return Math.floor(mockStat(player.id, 'field') * 20 + 50);
      // Prospects (null → Infinity so they always sort last)
      case 'avgrank': return getAvgRank(player) ?? Infinity;
      case 'mlb': return getProspectRank(player, 'mlb', 'mlb.com', 'mlbpipeline', 'pipeline') ?? Infinity;
      case 'ba':  return getProspectRank(player, 'ba', 'baseball america', 'baseballamerica') ?? Infinity;
      case 'bp':  return getProspectRank(player, 'bp', 'baseball prospectus', 'baseballprospectus', 'prospectus') ?? Infinity;
      // Fixed columns
      case 'rating': return Math.floor(mockStat(player.id, 'rating') * 40 + 40); // 40–80
      case 'owned':  return Math.floor(mockStat(player.id, 'owned')  * 100);      // 0–100%
      default: return null;
    }
  };


  const loadPage = useCallback(async (token) => {
    try {
      setLoading(true);
      setError('');

      // Fixtures mode: serve the whole filtered sample pool from memory (no API,
      // no server pagination) so the page renders a full, realistic list.
      if (USE_ROSTER_FIXTURES) {
        let pool = getSampleFreeAgents();
        if (filters.position) {
          pool = pool.filter((p) => (p.positions || [p.position]).includes(filters.position));
        }
        if (filters.team) pool = pool.filter((p) => p.team === filters.team);
        const rostered = new Set(rosteredPlayerIds.map(String));
        if (filters.playerFilter === 'available' && rostered.size > 0) {
          pool = pool.filter((p) => !rostered.has(String(p.id ?? p.playerId)));
        } else if (filters.playerFilter === 'taken' && rostered.size > 0) {
          pool = pool.filter((p) => rostered.has(String(p.id ?? p.playerId)));
        }
        setPlayers(pool);
        setNextToken(null);
        setLoading(false);
        return;
      }

      const result = await playerService.getPlayersPage({
        limit: PAGE_SIZE,
        nextToken: token || undefined,
        filters: { position: filters.position || undefined, team: filters.team || undefined },
      });
      if (!result.success) throw new Error(result.error || 'Failed to load players');
      let pagePlayers = result.players || [];
      const rosteredSet = new Set(rosteredPlayerIds.map(String));
      if (filters.playerFilter === 'available' && rosteredSet.size > 0) {
        pagePlayers = pagePlayers.filter(p => { const id = (p.id ?? p.playerId ?? p.fantasyDataPlayerId)?.toString(); return id && !rosteredSet.has(id); });
      } else if (filters.playerFilter === 'taken' && rosteredSet.size > 0) {
        pagePlayers = pagePlayers.filter(p => { const id = (p.id ?? p.playerId ?? p.fantasyDataPlayerId)?.toString(); return id && rosteredSet.has(id); });
      }
      setPlayers(pagePlayers);
      setNextToken(result.nextToken ?? null);
    } catch {
      setError('Failed to load players. Please try again later.');
      setPlayers([]);
      setNextToken(null);
    } finally {
      setLoading(false);
    }
  }, [filters.position, filters.team, filters.playerFilter, rosteredPlayerIds]);

  useEffect(() => { loadPage(null); setPageTokens([null]); }, [filters.position, filters.team, filters.playerFilter, leagueId, loadPage]);

  const handleNextPage = async () => { if (!nextToken) return; setPageTokens(p => [...p, nextToken]); await loadPage(nextToken); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const handlePrevPage = async () => { if (pageTokens.length <= 1) return; const prev = pageTokens.slice(0, -1); setPageTokens(prev); await loadPage(prev[prev.length - 1]); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const filteredPlayers = React.useMemo(() => {
    let list = players;
    if (filters.playerType === 'hitters') list = list.filter(p => !p.isPitcher);
    else if (filters.playerType === 'pitchers') list = list.filter(p => p.isPitcher);
    else if (filters.playerType === 'prospects') list = list.filter(p => p.prospectRankings || p.prospectEligible);
    if (filters.search) { const q = filters.search.toLowerCase(); list = list.filter(p => (p.name || '').toLowerCase().includes(q)); }
    return list;
  }, [players, filters.playerType, filters.search]);

  const displayPlayers = React.useMemo(() => {
    if (!sortConfig.col) return filteredPlayers;
    return [...filteredPlayers].sort((a, b) => {
      const av = getSortValue({ ...a, stats: a.stats || mapStatsFromAPI(playerStats[a.id] || {}) }, sortConfig.col);
      const bv = getSortValue({ ...b, stats: b.stats || mapStatsFromAPI(playerStats[b.id] || {}) }, sortConfig.col);
      // Always put null/Infinity last regardless of direction
      const aN = av == null || av === Infinity;
      const bN = bv == null || bv === Infinity;
      if (aN && bN) return 0;
      if (aN) return 1;
      if (bN) return -1;
      if (typeof av === 'string') return sortConfig.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortConfig.dir === 'asc' ? av - bv : bv - av;
    });
  }, [filteredPlayers, sortConfig, playerStats]);

  const getDisplayPlayers = () => displayPlayers.map(p => ({ ...p, stats: p.stats || mapStatsFromAPI(playerStats[p.id] || {}) }));

  const handleAddToRoster = async (player) => {
    const { getRosterForDate, saveRosterForDate, addPlayerToRoster, getCurrentDate } = await import('../../utils/rosterManager');
    const currentDate = getCurrentDate();
    const userId = user?.userId || user?.id || user?.sub;
    const currentRoster = await getRosterForDate(userId, leagueId, currentDate, league);
    const result = addPlayerToRoster(currentRoster, { ...player, stats: { battingAverage: player.stats?.avg ?? 0, homeRuns: player.stats?.hr ?? 0, rbi: player.stats?.rbi ?? 0, stolenBases: player.stats?.sb ?? 0 } });
    if (result.success) {
      await saveRosterForDate(userId, leagueId, result.roster, currentDate, league);
      if (filters.playerFilter === 'available') setPlayers(prev => prev.filter(p => (p.id ?? p.playerId) !== (player.id ?? player.playerId)));
      alert(`${player.name} added to your roster!`);
    } else { alert(result.error); }
  };

  // The add button opens a floating confirm bar (mirrors the roster's drop bar):
  // add immediately, or add and jump to My Roster to drop a player to make room.
  const confirmAdd = () => {
    if (pendingAdd) handleAddToRoster(pendingAdd);
    setPendingAdd(null);
  };
  const addThenDrop = () => {
    const p = pendingAdd;
    setPendingAdd(null);
    if (p) handleAddToRoster(p);
    navigate(`/league/${leagueId}/roster`);
  };

  // Dismiss the add-confirmation bar with Escape.
  useEffect(() => {
    if (!pendingAdd) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPendingAdd(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingAdd]);

  const isProspects = viewMode === 'research' && researchSubTab === 'prospects';
  const sharedProps = { sortConfig, onSort: handleSort };

  // ---- Column headers ----
  const renderColHeaders = () => {
    if (viewMode === 'stats') {
      return (
        <>
          {filters.playerType !== 'pitchers' && (
            <><th style={{ textAlign: 'center', whiteSpace: 'nowrap', letterSpacing: '-0.022em' }}>H/AB</th><SortTh label="AVG" col="avg" {...sharedProps} /><SortTh label="HR" col="hr" {...sharedProps} /><SortTh label="RBI" col="rbi" {...sharedProps} /><SortTh label="R" col="r" {...sharedProps} /><SortTh label="SB" col="sb" {...sharedProps} /></>
          )}
          {filters.playerType !== 'hitters' && (
            <><SortTh label="ERA" col="era" {...sharedProps} /><SortTh label="WHIP" col="whip" {...sharedProps} /><SortTh label="K" col="k" {...sharedProps} /><SortTh label="W" col="w" {...sharedProps} /><SortTh label="SV" col="sv" {...sharedProps} /></>
          )}
        </>
      );
    }
    if (viewMode === 'research') {
      if (researchSubTab === 'prospects') return (
        <>
          <SortTh label="Avg Rank" col="avgrank" {...sharedProps} />
          <SortTh label="MLB.com" col="mlb" {...sharedProps} />
          <SortTh label="Baseball America" col="ba" {...sharedProps} minWidth="130px" />
          <SortTh label="Baseball Prospectus" col="bp" {...sharedProps} minWidth="150px" />
        </>
      );
      if (researchSubTab === 'trends') {
        if (filters.playerType !== 'pitchers') return <><SortTh label="OBP" col="obp" {...sharedProps} /><SortTh label="SLG" col="slg" {...sharedProps} /><SortTh label="OPS" col="ops" {...sharedProps} /><SortTh label="wOBA" col="woba" {...sharedProps} /><SortTh label="wRC+" col="wrc" {...sharedProps} /></>;
        return <><SortTh label="FIP" col="fip" {...sharedProps} /><SortTh label="xFIP" col="xfip" {...sharedProps} /><SortTh label="K/9" col="k9" {...sharedProps} /><SortTh label="BB/9" col="bb9" {...sharedProps} /><SortTh label="HR/9" col="hr9" {...sharedProps} /></>;
      }
      if (researchSubTab === 'ratings') return <><SortTh label="Contact" col="contact" {...sharedProps} /><SortTh label="Power" col="power" {...sharedProps} /><SortTh label="Speed" col="speed" {...sharedProps} /><SortTh label="Arm" col="arm" {...sharedProps} /><SortTh label="Field" col="field" {...sharedProps} /></>;
    }
    if (viewMode === 'schedule') {
      return scheduleDays.map((d, i) => <th key={i} style={{ textAlign: 'center', fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDateHeader(d)}</th>);
    }
    return null;
  };

  // ---- Rank badge cell ----
  const RankCell = ({ rank }) => (
    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
      {rank != null ? (
        <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: '9999px', background: 'var(--primary-soft)', color: 'var(--primary)', fontSize: '12px', fontWeight: '700', letterSpacing: '-0.022em' }}>
          #{rank}
        </span>
      ) : <span style={{ color: 'var(--muted)', fontSize: '13px' }}>—</span>}
    </td>
  );

  const td = (val, key) => <td key={key} className="rz-c-stat rz-num">{val ?? '—'}</td>;

  // ---- Data cells ----
  const renderDataCells = (player) => {
    if (viewMode === 'stats') {
      return (
        <>
          {filters.playerType !== 'pitchers' && (!player.isPitcher
            ? <>{td(player.stats.atBats ? `${player.stats.hits ?? 0}/${player.stats.atBats}` : '—', 'hab')}{td(player.stats.avg > 0 ? player.stats.avg.toFixed(3) : '—', 'avg')}{td(player.stats.hr || '—', 'hr')}{td(player.stats.rbi || '—', 'rbi')}{td(player.stats.r || '—', 'r')}{td(player.stats.sb || '—', 'sb')}</>
            : <>{td('—','z')}{td('—','a')}{td('—','b')}{td('—','c')}{td('—','d')}{td('—','e')}</>
          )}
          {filters.playerType !== 'hitters' && (player.isPitcher
            ? <>{td(player.stats.era > 0 ? player.stats.era.toFixed(2) : '—', 'era')}{td(player.stats.whip > 0 ? player.stats.whip.toFixed(2) : '—', 'whip')}{td(player.stats.k || '—', 'k')}{td(player.stats.w || '—', 'w')}{td(player.stats.sv || '—', 'sv')}</>
            : <>{td('—','f')}{td('—','g')}{td('—','h')}{td('—','i')}{td('—','j')}</>
          )}
        </>
      );
    }

    if (viewMode === 'research') {
      const id = player.id;
      if (researchSubTab === 'prospects') {
        return (
          <>
            <RankCell rank={getAvgRank(player)} />
            <RankCell rank={getProspectRank(player, 'mlb', 'mlb.com', 'mlbpipeline', 'pipeline')} />
            <RankCell rank={getProspectRank(player, 'ba', 'baseball america', 'baseballamerica')} />
            <RankCell rank={getProspectRank(player, 'bp', 'baseball prospectus', 'baseballprospectus', 'prospectus')} />
          </>
        );
      }
      if (researchSubTab === 'trends') {
        if (!player.isPitcher && filters.playerType !== 'pitchers') {
          return (
            <>
              {td('.' + (Math.floor(mockStat(id, 'obp') * 200 + 300)), 'obp')}
              {td('.' + (Math.floor(mockStat(id, 'slg') * 200 + 400)), 'slg')}
              {td('.' + (Math.floor(mockStat(id, 'ops') * 300 + 700)), 'ops')}
              {td('.' + (Math.floor(mockStat(id, 'woba') * 100 + 300)), 'woba')}
              {td(Math.floor(mockStat(id, 'wrc') * 50 + 80), 'wrc')}
            </>
          );
        }
        return (
          <>
            {td((mockStat(id, 'fip') * 2 + 3).toFixed(2), 'fip')}
            {td((mockStat(id, 'xfip') * 2 + 3).toFixed(2), 'xfip')}
            {td((mockStat(id, 'k9') * 4 + 8).toFixed(1), 'k9')}
            {td((mockStat(id, 'bb9') * 2 + 2).toFixed(1), 'bb9')}
            {td((mockStat(id, 'hr9') * 0.5 + 0.8).toFixed(1), 'hr9')}
          </>
        );
      }
      if (researchSubTab === 'ratings') {
        return (
          <>
            {td(Math.floor(mockStat(id, 'contact') * 20 + 50), 'ct')}
            {td(Math.floor(mockStat(id, 'power') * 20 + 50), 'pw')}
            {td(Math.floor(mockStat(id, 'speed') * 20 + 50), 'sp')}
            {td(Math.floor(mockStat(id, 'arm') * 20 + 50), 'arm')}
            {td(Math.floor(mockStat(id, 'field') * 20 + 50), 'fld')}
          </>
        );
      }
    }

    if (viewMode === 'schedule') {
      return scheduleDays.map((day, i) => {
        // Seed off player + day string so each day is independently determined
        const dayKey = 'sched' + i + day.toDateString();
        const g = getGameInfo(player.id + dayKey);
        return (
          <td key={i} style={{ padding: '6px 8px', textAlign: 'center', fontSize: '12px', letterSpacing: '-0.022em', color: g ? 'var(--foreground)' : 'var(--muted)', whiteSpace: 'nowrap' }}>
            {g ? g.opponent : ''}
          </td>
        );
      });
    }
    return null;
  };

  const positions = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'OF', 'DH', 'P', 'SP', 'RP'];
  const teams = ['NYY', 'BOS', 'TB', 'TOR', 'BAL', 'CLE', 'DET', 'KC', 'MIN', 'CWS', 'HOU', 'LAA', 'OAK', 'SEA', 'TEX', 'ATL', 'MIA', 'NYM', 'PHI', 'WSH', 'CHC', 'CIN', 'MIL', 'PIT', 'STL', 'ARI', 'COL', 'LAD', 'SD', 'SF'];
  const skeletonCols = isProspects ? 4 : viewMode === 'schedule' ? scheduleDays.length : filters.playerType === 'all' ? 11 : filters.playerType === 'pitchers' ? 5 : 6;

  if (error) return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
        <h2 style={{ letterSpacing: '-0.022em', marginBottom: '8px' }}>Error Loading Players</h2>
        <p style={{ color: 'var(--danger)', marginBottom: '16px' }}>{error}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>Retry</button>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

      {/* Filters — compact design-system bar (matches the roster's dropdowns) */}
      <div className="players-filters">
        <input
          className="players-search"
          type="text"
          placeholder="Search players…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          aria-label="Search players"
        />
        <div className="rz-pselect">
          <select value={filters.playerType} onChange={(e) => setFilters({ ...filters, playerType: e.target.value, position: '' })} aria-label="Player type">
            <option value="all">All Players</option>
            <option value="hitters">Hitters</option>
            <option value="pitchers">Pitchers</option>
            <option value="prospects">Prospects</option>
          </select>
          <span className="rz-pcaret">▾</span>
        </div>
        <div className="rz-pselect">
          <select value={filters.position} onChange={(e) => setFilters({ ...filters, position: e.target.value })} aria-label="Position">
            <option value="">All Positions</option>
            {positions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <span className="rz-pcaret">▾</span>
        </div>
        <div className="rz-pselect">
          <select value={filters.team} onChange={(e) => setFilters({ ...filters, team: e.target.value })} aria-label="Team">
            <option value="">All Teams</option>
            {teams.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="rz-pcaret">▾</span>
        </div>
        <div className="rz-pselect">
          <select value={filters.playerFilter} onChange={(e) => setFilters({ ...filters, playerFilter: e.target.value })} aria-label="Availability">
            <option value="available">Available</option>
            <option value="all">All</option>
            <option value="taken">Taken</option>
          </select>
          <span className="rz-pcaret">▾</span>
        </div>
        <span className="players-count">
          {displayPlayers.length} players
          {sortConfig.col && (
            <>
              {' · '}sorted by {sortConfig.col} {sortConfig.dir === 'asc' ? '▲' : '▼'}
              <button type="button" className="players-clear-sort" onClick={() => setSortConfig({ col: null, dir: 'desc' })}>clear ×</button>
            </>
          )}
        </span>
      </div>

      {/* View-mode control — matches the roster page's segmented control (.rz-seg) */}
      <div className="rz-toolbar" style={{ marginBottom: '12px' }}>
        <div className="rz-viewgroup">
          <div className="rz-seg" role="tablist">
            {[{ id: 'stats', label: 'Stats' }, { id: 'research', label: 'Research' }, { id: 'schedule', label: 'Schedule' }].map(tab => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={viewMode === tab.id}
                className={viewMode === tab.id ? 'on' : ''}
                onClick={() => setViewMode(tab.id)}
              >
                {tab.label}
              </button>
            ))}
            {viewMode === 'stats' && (
              <select className="rz-subselect" aria-label="Stat period" value={statPeriod} onChange={(e) => setStatPeriod(e.target.value)}>
                <option value="season">2026 Season</option>
                <option value="last15">Last 15 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="last60">Last 60 Days</option>
                <option value="previous">2025 Season</option>
              </select>
            )}
            {viewMode === 'research' && (
              <select className="rz-subselect" aria-label="Research view" value={researchSubTab} onChange={(e) => setResearchSubTab(e.target.value)}>
                <option value="trends">Trends</option>
                <option value="ratings">Ratings</option>
                <option value="prospects">Prospects</option>
              </select>
            )}
            {viewMode === 'schedule' && (
              <select className="rz-subselect" aria-label="Schedule range" value={scheduleSubTab} onChange={(e) => setScheduleSubTab(e.target.value)}>
                <option value="week">Week</option>
                <option value="full">Full</option>
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      {(loading || players.length > 0) ? (
        <React.Fragment>
          <div className="rz-card">
            <div className="rz-tbl-wrap rz-tbl-wrap-players">
              <table className="rz-tbl rz-tbl-players">
                <thead>
                  <tr>
                    <th className="rz-c-player" style={{ width: `${COL_W.player}px`, minWidth: `${COL_W.player}px`, letterSpacing: '-0.022em' }}>Player</th>
                    {/* Fixed columns – always visible. Widths must match COL_W. */}
                    <th style={{ textAlign: 'left', whiteSpace: 'nowrap', width: `${COL_W.game}px`, minWidth: `${COL_W.game}px`, letterSpacing: '-0.022em' }}>Game</th>
                    <SortTh label="Rating" col="rating" align="center" sortConfig={sortConfig} onSort={handleSort} width={`${COL_W.rating}px`} />
                    <SortTh label="Own%" col="owned" align="center" sortConfig={sortConfig} onSort={handleSort} width={`${COL_W.owned}px`} />
                    {/* Tab-specific columns */}
                    {renderColHeaders()}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(PAGE_SIZE)].map((_, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: 32, height: 32, borderRadius: '6px', background: 'var(--surface-muted)' }} />
                            <div>
                              <div style={{ height: 13, width: 90, background: 'var(--surface-muted)', borderRadius: 4, marginBottom: 4 }} />
                              <div style={{ height: 11, width: 70, background: 'var(--surface-muted)', borderRadius: 4 }} />
                            </div>
                          </div>
                        </td>
                        {/* 3 fixed + skeletonCols tab-specific + 1 action */}
                        {[...Array(skeletonCols + 3)].map((_, j) => (
                          <td key={j} style={{ padding: '8px', textAlign: 'center' }}>
                            <div style={{ height: 13, width: 30, background: 'var(--surface-muted)', borderRadius: 4, margin: '0 auto' }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    getDisplayPlayers().map((player, index) => {
                      const gs = getPlayerGameStatus(player, mlbGames);
                      const game = !gs ? null : {
                        opponent: gs.opponent,
                        status: gs.kind === 'pre' ? 'scheduled' : gs.kind,
                        time: gs.kind === 'live' ? gs.inningText : gs.kind === 'final' ? 'Final' : gs.timeText,
                        score: gs.kind === 'pre' ? null : gs.score,
                      };
                      const rating = Math.floor(mockStat(player.id, 'rating') * 40 + 40);
                      // Pre-draft: nobody's rostered yet, so ownership is 0%.
                      const owned  = phase === 'pre-draft' ? 0 : Math.floor(mockStat(player.id, 'owned') * 100);
                      const statusColor = game?.status === 'live' ? 'var(--success)' : game?.status === 'final' ? 'var(--muted)' : 'var(--primary)';
                      const photo = player.photoUrl || player.headshotUrl || player.image;
                      const elig = (player.positions || [player.position]).filter((p) => p && p !== 'UTIL' && p !== 'P').slice(0, 4).join(', ');
                      const openProfile = () => openPlayerProfile(player, league.id || league.leagueId);
                      return (
                        <tr key={player.id} className="rz-row rz-filled">
                          {/* Player — roster-style cell (add button + rz-avatar + name + meta) */}
                          <td className="rz-c-player">
                            <div className="rz-player-cell">
                              <button type="button" className="btn btn-success btn-icon btn-sm rz-add-btn" onClick={() => setPendingAdd(player)} title="Add to roster" aria-label="Add to roster">
                                <PlusIcon size={14} />
                              </button>
                              <button type="button" className={`rz-avatar ${player.isPitcher ? 'pitch' : 'hit'}`} onClick={openProfile} title="Player profile" aria-label={`${player.name} profile`}>
                                {photo ? <img src={photo} alt="" /> : <PlayerSilhouette />}
                              </button>
                              <div className="rz-pc-main">
                                <div className="rz-pc-name"><button type="button" className="rz-name-btn" onClick={openProfile}>{player.name}</button></div>
                                <div className="rz-pc-meta">{player.team || 'FA'}{elig && <span className="rz-elig"> &middot; {elig}</span>}</div>
                              </div>
                            </div>
                          </td>

                          {/* Fixed: Game */}
                          <td style={{ padding: '6px 8px', verticalAlign: 'middle' }}>
                            {game ? (
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: '500', letterSpacing: '-0.022em', color: 'var(--foreground)', whiteSpace: 'nowrap' }}>
                                  {game.opponent}
                                </div>
                                <div style={{ fontSize: '11px', color: statusColor, marginTop: '1px', letterSpacing: '-0.022em', whiteSpace: 'nowrap' }}>
                                  {game.time}{game.score ? ` · ${game.score}` : ''}
                                </div>
                              </div>
                            ) : (
                              <span style={{ fontSize: '12px', color: 'var(--muted)', letterSpacing: '-0.022em' }}>Off</span>
                            )}
                          </td>

                          {/* Fixed: Rating */}
                          <td style={{ padding: '6px 8px', textAlign: 'center', verticalAlign: 'middle' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: '34px', height: '22px', borderRadius: '6px',
                              background: ratingHeatColor(rating).bg,
                              color: ratingHeatColor(rating).fg,
                              border: '1px solid rgba(0,0,0,0.08)',
                              fontSize: '12px', fontWeight: '700', letterSpacing: '-0.022em',
                            }}>
                              {rating}
                            </span>
                          </td>

                          {/* Fixed: Own% */}
                          <td style={{ padding: '6px 8px', textAlign: 'center', verticalAlign: 'middle' }}>
                            <span style={{
                              fontSize: '12px', fontWeight: '600', letterSpacing: '-0.022em',
                              color: owned >= 70 ? 'var(--success)' : owned >= 30 ? 'var(--foreground)' : 'var(--muted)',
                            }}>
                              {owned}%
                            </span>
                          </td>

                          {/* Tab-specific columns */}
                          {renderDataCells(player)}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {(pageTokens.length > 1 || !!nextToken) && (
            <div className="card" style={{ padding: '14px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px' }}>
                <button className="btn btn-secondary btn-sm" onClick={handlePrevPage} disabled={pageTokens.length <= 1} style={{ opacity: pageTokens.length > 1 ? 1 : 0.4 }}>Previous</button>
                <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--muted)', letterSpacing: '-0.022em', minWidth: '72px', textAlign: 'center' }}>Page {pageTokens.length}</span>
                <button className="btn btn-secondary btn-sm" onClick={handleNextPage} disabled={!nextToken} style={{ opacity: nextToken ? 1 : 0.4 }}>Next</button>
              </div>
            </div>
          )}
        </React.Fragment>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
          <div style={{ fontSize: '36px', marginBottom: '12px', opacity: 0.4 }}>⚾</div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', letterSpacing: '-0.022em', marginBottom: '8px' }}>No players found</h3>
          <p style={{ color: 'var(--muted)', fontSize: '15px', letterSpacing: '-0.022em' }}>Try adjusting your filters to see more players.</p>
        </div>
      )}

      {/* Floating add-confirmation bar — same treatment as the roster's drop bar */}
      {pendingAdd && (
        <div className="rz-dropbar" role="dialog" aria-modal="false" aria-label="Confirm add">
          <div className="rz-dropbar-msg">
            Add <strong>{pendingAdd.name}</strong>
            {pendingAdd.position ? ` · ${pendingAdd.position}` : ''} to your roster?
          </div>
          <div className="rz-dropbar-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPendingAdd(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addThenDrop}>
              Drop a Player
            </button>
            <button type="button" className="btn btn-success btn-sm" onClick={confirmAdd}>
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeaguePlayers;
